import { Model } from "@react3l/react3l/core/model";
import { commonService } from "@react3l/react3l/services";
import { AxiosError } from "axios";
import React, { useCallback, useMemo, useState } from "react";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import appMessageService from "services/app-message-service";
import { formService } from "services/form-service";
import { queryStringService } from "services/query-string-service";
import { routerService } from "services/route-service";

const useContentList = <T extends Model, TContent extends Model>(
  model: T,
  setModel: (model: T) => void,
  contentField: string
) => {
  const content: TContent[] = useMemo(() => {
    if (model) {
      if (typeof model[contentField] === "undefined") return null;
      if (model[contentField]?.length === 0) return [];
      if (model[contentField]?.length > 0) {
        return model[contentField].map((item, index) => {
          if (typeof item.key !== "undefined") {
            return { ...item };
          }
          return { ...item, key: index };
        });
      }
    }
  }, [contentField, model]);

  const setContent = useCallback(
    (v: TContent[]) => {
      setModel({ ...model, [contentField]: v });
    },
    [contentField, setModel, model]
  );

  return { content, setContent };
};

const useDetail = <T extends Model>(
  ModelClass: new () => T,
  getDetail: (id: number) => Observable<T>,
  saveModel: (t: T) => Observable<T>,
  routePrefix: string,
  initData?: T
) => {
  const { id }: any = queryStringService.useGetQueryString();
  const [, , , handleGoBase] = routerService.useMasterNavigation(routePrefix);
  const {
    notifyUpdateItemSuccess,
    notifyUpdateItemError,
  } = appMessageService.useCRUDMessage();
  const [subscription] = commonService.useSubscription();

  const isDetail = id !== "";

  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<{ status?: number; message?: string } | null>(null);

  const [
    model,
    handleChangeSimpleField,
    handleChangeObjectField,
    handleUpdateNewModel,
    handleChangeTreeObjectField,
    dispatch,
  ] = formService.useDetailForm<T>(ModelClass, id, getDetail, initData);

  // Track loading errors
  React.useEffect(() => {
    if (id) {
      setLoadError(null);
      subscription.add(
        getDetail(id).subscribe({
          error: (error: AxiosError) => {
            setLoadError({
              status: error.response?.status,
              message: error.message,
            });
          },
        })
      );
    }
  }, [id, getDetail, subscription]);

  const handleSave = useCallback(
    (onSaveSuccess?: (item: T) => void, onSaveError?: (item: T) => void) => {
      return () => {
        setLoading(true);
        subscription.add(
          saveModel(model)
            .pipe(finalize(() => setLoading(false)))
            .subscribe(
              (item: T) => {
                handleUpdateNewModel(item);
                handleGoBase();
                notifyUpdateItemSuccess();
                localStorage.removeItem(ModelClass.name);
                if (typeof onSaveSuccess === "function") {
                  onSaveSuccess(item);
                }
              },
              (error: AxiosError<T>) => {
                if (error.response && error.response.status === 400) {
                  handleUpdateNewModel(error.response?.data);
                }
                notifyUpdateItemError();
                if (typeof onSaveError === "function") {
                  onSaveError(error.response?.data);
                }
              }
            )
        );
      };
    },
    [
      ModelClass.name,
      handleGoBase,
      handleUpdateNewModel,
      model,
      notifyUpdateItemError,
      notifyUpdateItemSuccess,
      saveModel,
      subscription,
    ]
  );
  return {
    model,
    isDetail,
    handleChangeSimpleField,
    handleChangeObjectField,
    handleUpdateNewModel,
    handleChangeTreeObjectField,
    loading,
    loadError,
    handleGoBase,
    handleSave,
    dispatch,
  };
};

const useDetailModal = <T extends Model>(
  ModelClass: new () => T,
  getDetail: (id: number | string) => Observable<T>,
  saveModel: (t: T, isDetail: boolean) => Observable<T>,
  handleSeach?: () => void
) => {
  const {
    notifyUpdateItemSuccess,
    notifyUpdateItemError,
  } = appMessageService.useCRUDMessage();

  const [subscription] = commonService.useSubscription();

  const [isOpenDetailModal, setIsOpenDetailModal] = useState<boolean>(false);
  const [loadingModel, setLoadingModel] = useState<boolean>(false);

  const [isDetailModal, setIsDetailModal] = useState<boolean>(false);

  const [
    model,
    handleChangeSimpleField,
    handleChangeObjectField,
    handleUpdateNewModel,
    handleChangeTreeObjectField,
    dispatch,
  ] = formService.useDetailForm<T>(ModelClass, undefined, getDetail);

  const handleOpenDetailModal = useCallback(
    (id: number | string) => {
      setIsOpenDetailModal(true);
      if (id) {
        setIsDetailModal(true);
        setLoadingModel(true);
        subscription.add(
          getDetail(id)
            .pipe(finalize(() => setLoadingModel(false)))
            .subscribe((item: T) => {
              handleUpdateNewModel(item);
            })
        );
      } else {
        handleUpdateNewModel(new ModelClass());
      }
    },
    [getDetail, handleUpdateNewModel, subscription, ModelClass]
  );

  const handleSaveModel = useCallback(() => {
    setLoadingModel(true);
    subscription.add(
      saveModel(model, isDetailModal)
        .pipe(
          finalize(() => {
            setLoadingModel(false);
            setIsDetailModal(false);
          })
        )
        .subscribe(
          (item: T) => {
            handleUpdateNewModel(item);
            setIsOpenDetailModal(false);
            if (typeof handleSeach === "function") handleSeach();
            notifyUpdateItemSuccess();
          },
          (error: AxiosError<T>) => {
            if (error.response && error.response.status === 400)
              handleUpdateNewModel(error.response?.data);
            notifyUpdateItemError();
          }
        )
    );
  }, [
    saveModel,
    isDetailModal,
    subscription,
    handleSeach,
    notifyUpdateItemError,
    notifyUpdateItemSuccess,
    handleUpdateNewModel,
    model,
  ]);

  const handleCloseDetailModal = useCallback(() => {
    setIsOpenDetailModal(false);
    setIsDetailModal(false);
    if (model.id) handleUpdateNewModel({ ...model });
    else handleUpdateNewModel({ ...new ModelClass() });
    if (typeof handleSeach === "function") handleSeach();
  }, [handleSeach, ModelClass, handleUpdateNewModel, model]);

  return {
    model,
    isOpenDetailModal,
    isDetailModal,
    loadingModel,
    handleOpenDetailModal,
    handleSaveModel,
    handleChangeSimpleField,
    handleChangeObjectField,
    handleChangeTreeObjectField,
    handleCloseDetailModal,
    dispatch,
    handleUpdateNewModel,
  };
};

const detailService = {
  useContentList,
  useDetail,
  useDetailModal,
};

export default detailService;
